import moment from "moment";
import React from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import { userState } from "../../redux/reducers/userReducer";
import { Icon } from "../classes/schedule/ui";
import { BookingsChartCard, ComingUp, RecentBookings, useSchedule } from "./insights/BookingsInsights";
import { KpiTiles, useKpis } from "./insights/KpiTiles";
import { FollowUpList, FollowUpSection, NewMembersCard, useMemberInsights } from "./insights/MembersInsights";
import { RevenueChartCard, Transactions } from "./insights/RevenueInsights";

/**
 * Club dashboard (Home, /home/dashboard).
 * Home view: Members / Bookings / Revenue tiles, coming-up sessions, members to follow up, recent transactions.
 * Overview: the greeting, the three tiles and the overview cards. Clicking a tile shows that area's insights
 * below the tiles (?view=members|bookings|revenue); the tile turns its colour, clicking it again (or "Overview")
 * goes back to the overview. The tiles are the only switch (no separate tabs).
 * Club Stats (/my-stats) now redirects here.
 */

const VIEWS = { members: "Members", bookings: "Bookings", revenue: "Revenue" };

const greeting = () => {
  const h = moment().hour();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
};

export default function ClubDashboard() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const userReduxState = useSelector(userState);
  const view = VIEWS[params.get("view")] ? params.get("view") : "home";
  const [openInsight, setOpenInsight] = React.useState(null);

  const insights = useMemberInsights();
  const schedule = useSchedule();
  const kpis = useKpis();
  const memberIndex = insights.lists?.byId;

  const go = React.useCallback(
    (v, insight = null) => {
      const next = new URLSearchParams(params);
      if (v === "home") next.delete("view");
      else next.set("view", v);
      setParams(next);
      setOpenInsight(insight);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [params, setParams]
  );

  const firstName = (userReduxState.userData?.fullName || "").split(" ")[0];
  const clubName = userReduxState.userData?.myBusinessProfile?.businessName || "your club";

  return (
    <div className="flex-1 px-4 pb-16 pt-6 sm:px-8 lg:px-10">
      <div>
        <h1 className="text-2xl font-black tracking-tight text-gray-900 sm:text-[28px]">
          {greeting()}
          {firstName ? `, ${firstName}` : ""}
        </h1>
        <p className="mt-1 text-sm text-gray-600 sm:text-[15px]">
          {moment().format("dddd D MMMM")} · here's what's happening at {clubName}
        </p>
      </div>

      {view !== "home" && (
        <button
          type="button"
          onClick={() => go("home")}
          className="mt-4 inline-flex h-9 items-center gap-1.5 rounded-xl bg-[#F3ECF5] px-3.5 text-sm font-extrabold text-brown-coffee transition-colors hover:bg-brown-coffee hover:text-white"
        >
          <Icon className="h-4 w-4" d={<path d="m15 18-6-6 6-6" />} />
          Overview
        </button>
      )}

      <div className="mt-5">
        <KpiTiles kpis={kpis} active={view === "home" ? null : view} onOpen={(k) => go(k === view ? "home" : k)} />
      </div>

      {view !== "home" && (
        <h2 className="mt-6 text-xl font-black tracking-tight text-gray-900">{VIEWS[view]} insights</h2>
      )}

      {view === "home" && (
        <>
          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.65fr)_minmax(0,1fr)]">
            <ComingUp schedule={schedule} limit={6} />
            <FollowUpList insights={insights} onOpen={(k) => go("members", k)} onSeeAll={() => go("members")} />
          </div>
          <div className="mt-4">
            <Transactions compact memberIndex={memberIndex} onViewAll={() => go("revenue")} />
          </div>
        </>
      )}

      {view === "members" && (
        <div className="mt-6 flex flex-col gap-5">
          <FollowUpSection insights={insights} openKey={openInsight} setOpenKey={setOpenInsight} />
          <NewMembersCard />
          <div className="flex justify-end">
            <button type="button" onClick={() => navigate("/mymembers")} className="rounded-lg px-2 py-1.5 text-[13px] font-extrabold text-indigo-bz hover:bg-[#F3E9FF]">
              Go to My Members ›
            </button>
          </div>
        </div>
      )}

      {view === "bookings" && (
        <div className="mt-6 flex flex-col gap-4">
          <BookingsChartCard />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
            <ComingUp schedule={schedule} limit={12} subtitle="Bookings for your next sessions" />
            <RecentBookings memberIndex={memberIndex} limit={8} />
          </div>
        </div>
      )}

      {view === "revenue" && (
        <div className="mt-6 flex flex-col gap-4">
          <RevenueChartCard />
          <Transactions memberIndex={memberIndex} />
        </div>
      )}
    </div>
  );
}
